/**
 * Planning V2 — Cockpit hebdomadaire de l'agence.
 * ────────────────────────────────────────────────
 * Mission unique : « Quelles livraisons dois-je préparer aujourd'hui / cette semaine ? »
 *
 * Structure premium (wyvio-ui) :
 *   1. Toolbar (titre + segmented range + team filter)
 *   2. Bannière conflits (le seul bloc rouge, uniquement s'il y en a)
 *   3. WeekStrip : sprint de 7 jours avec pastille compteur — navigation rapide mobile
 *   4. Day sections : chaque jour = un H2 + une colonne verticale de cartes
 *   5. Card V2 : heure XL prominent + véhicule + client + chip statut
 *   6. Click carte → PlanningEventDrawer (ouvre dossier / modifier / message / confirmer)
 *
 * Aucune ligne rouge inutile, aucun bloc doublon. Le logiciel guide.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar, AlertTriangle, ChevronRight, MessageCircle, Truck, User, MapPin,
} from "lucide-react";
import {
  Card, Toolbar, SegmentedControl, MicroLabel, Chip,
} from "@/components/wyvio-ui";
import PlanningEventDrawer, { StatusChip } from "@/components/planning/PlanningEventDrawer";
import DeliverySlotsSheet from "@/components/dossier/DeliverySlotsSheet";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RANGES = [
  { key: "today", label: "Aujourd'hui" },
  { key: "week",  label: "7 jours" },
  { key: "month", label: "Mois" },
];

const FR_DAYS_SHORT = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"];
const FR_MONTHS = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];

const fmtDayLong = (iso) => {
  const d = new Date(iso);
  const day = ["dimanche","lundi","mardi","mercredi","jeudi","vendredi","samedi"][d.getDay()];
  return `${day} ${d.getDate()} ${FR_MONTHS[d.getMonth()]}`;
};
const isSameDay = (isoA, isoB) => {
  if (!isoA || !isoB) return false;
  return isoA.slice(0, 10) === isoB.slice(0, 10);
};
const isTodayISO = (iso) => isSameDay(iso, new Date().toISOString());

const groupByDate = (events) => {
  const map = {};
  for (const ev of events) (map[ev.date] = map[ev.date] || []).push(ev);
  return Object.entries(map)
    .map(([day, list]) => [day, list.sort((a, b) => (a.time || "").localeCompare(b.time || ""))])
    .sort(([a], [b]) => a.localeCompare(b));
};

// Bandeau des 7 prochains jours avec compteur d'événements/jour
const buildWeekStrip = (fromISO, events) => {
  const from = new Date(fromISO || new Date().toISOString());
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(from);
    d.setDate(from.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    days.push({
      iso,
      dayShort: FR_DAYS_SHORT[d.getDay()],
      dayNum: d.getDate(),
      count: events.filter((e) => e.date === iso).length,
      isToday: iso === new Date().toISOString().slice(0, 10),
    });
  }
  return days;
};

const Planning = () => {
  const navigate = useNavigate();
  const { agency } = useAuth();
  const [range, setRange] = useState("week");
  const [filter, setFilter] = useState("all");
  const [team, setTeam] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);        // event ouvert dans le drawer
  const [slotsFor, setSlotsFor] = useState(null);        // dossier_id en cours d'édition créneaux
  const [slotsCurrent, setSlotsCurrent] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [summary, planning] = await Promise.all([
        api.getDashboardSummary(filter === "all" ? null : filter),
        api.getPlanning(range, filter === "all" ? null : filter),
      ]);
      setTeam(summary.team || []);
      setData(planning);
      setError(null);
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range, filter]);

  const events = data?.events || [];
  const conflicts = data?.conflicts || [];
  const grouped = useMemo(() => groupByDate(events), [events]);
  const conflictKeys = new Set(conflicts.flatMap((c) => c.event_ids));
  const weekStrip = useMemo(() => buildWeekStrip(data?.from, events), [data, events]);

  const openDrawer = (ev) => setSelected(ev);
  const openDossier = (id) => navigate(`/app/dossier/${id}`);
  const openSlots = async (id) => {
    try {
      const r = await api.getDelivery(id).catch(() => null);
      setSlotsCurrent(r?.delivery || null);
    } catch {
      setSlotsCurrent(null);
    }
    setSelected(null);
    setSlotsFor(id);
  };

  const scrollToDay = (iso) => {
    const el = document.querySelector(`[data-testid="planning-day-${iso}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="wyvio-fade-in" data-testid="page-planning">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-6">
        <div>
          <MicroLabel>Agenda</MicroLabel>
          <h1 className="mt-2 text-[36px] sm:text-[42px] leading-[1.05] font-semibold tracking-[-0.03em] text-[#0A0A0A] dark:text-white">
            Planning
          </h1>
          <p className="mt-2 text-[14px] text-[#6B7280] dark:text-[#A3A3A3] max-w-xl">
            {agency?.name ? `${agency.name} · ` : ""}Les livraisons apparaissent ici dès qu'un créneau est confirmé.
          </p>
        </div>
        <Toolbar
          right={
            <>
              <SegmentedControl
                testId="planning-range-toggle"
                value={range}
                onChange={setRange}
                options={RANGES}
              />
              <select
                data-testid="planning-team-filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="h-11 rounded-full border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] px-4 text-[13px] font-medium text-[#0A0A0A] dark:text-white outline-none focus:border-[#0A0A0A] dark:focus:border-white transition-colors"
              >
                <option value="all">Toute l&apos;agence</option>
                {team.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </>
          }
        />
      </div>

      {/* Bandeau 7 jours pour navigation rapide (jamais rouge) */}
      {range === "week" && !loading && !error && weekStrip.length > 0 && (
        <div
          data-testid="planning-week-strip"
          className="mb-6 -mx-1 px-1 flex gap-2 overflow-x-auto pb-1 snap-x"
        >
          {weekStrip.map((d) => {
            const has = d.count > 0;
            return (
              <button
                key={d.iso}
                onClick={() => scrollToDay(d.iso)}
                disabled={!has}
                data-testid={`planning-week-day-${d.iso}`}
                className={[
                  "shrink-0 snap-start rounded-2xl border p-3 min-w-[76px] text-center transition-all duration-200",
                  d.isToday
                    ? "border-[#0A0A0A] bg-[#0A0A0A] text-white dark:bg-white dark:text-[#0A0A0A] dark:border-white"
                    : has
                    ? "border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] hover:border-[#0A0A0A]/20 dark:hover:border-white/20"
                    : "border-[#EEF2F5] dark:border-[#262626] bg-transparent opacity-60 cursor-default",
                ].join(" ")}
              >
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] opacity-80">
                  {d.dayShort}
                </p>
                <p className="mt-1 text-[22px] font-semibold tabular-nums leading-none">
                  {d.dayNum}
                </p>
                <p className="mt-1 text-[10.5px] font-medium opacity-80">
                  {d.count} liv.
                </p>
              </button>
            );
          })}
        </div>
      )}

      {/* Bannière conflits (unique bloc "alerte" toléré) */}
      {conflicts.length > 0 && (
        <Card className="mb-6 p-4 flex items-start gap-3 !border-[#FECACA] !bg-[#FEF2F2] dark:!bg-[#3B0F0F]/40" data-testid="planning-conflicts">
          <div className="h-9 w-9 rounded-2xl bg-[#EF4444] text-white flex items-center justify-center shrink-0">
            <AlertTriangle size={14} strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[#7F1D1D] dark:text-[#FCA5A5]">
              {conflicts.length} conflit{conflicts.length > 1 ? "s" : ""} détecté{conflicts.length > 1 ? "s" : ""}
            </p>
            <ul className="mt-1 text-[12px] text-[#991B1B] dark:text-[#FCA5A5]/80 space-y-0.5">
              {conflicts.map((c, i) => (
                <li key={i}>
                  {c.count} livraisons prévues le {fmtDayLong(c.date)} à {c.time}
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      {/* Contenu principal */}
      {loading ? (
        <div className="rounded-3xl border border-[#EEF2F5] dark:border-[#262626] p-10 text-center text-[13px] text-[#6B7280] dark:text-[#A3A3A3]">
          Chargement du planning…
        </div>
      ) : error ? (
        <Card className="p-4 !border-[#FECACA] !bg-[#FEF2F2] text-[13px] text-[#991B1B]">
          {error}
        </Card>
      ) : events.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-8" data-testid="planning-list">
          {grouped.map(([day, dayEvents]) => (
            <DaySection
              key={day}
              day={day}
              events={dayEvents}
              conflictKeys={conflictKeys}
              onClick={openDrawer}
            />
          ))}
        </div>
      )}

      <div className="h-20" />

      {/* Drawer d'actions rapides */}
      {selected && (
        <PlanningEventDrawer
          event={selected}
          onClose={() => setSelected(null)}
          onOpenDossier={(id) => { setSelected(null); openDossier(id); }}
          onOpenSlots={openSlots}
          onDone={async () => { setSelected(null); await load(); }}
        />
      )}

      {/* Modification créneaux depuis le planning */}
      {slotsFor && (
        <DeliverySlotsSheet
          dossierId={slotsFor}
          current={slotsCurrent}
          defaultLocation={slotsCurrent?.location || ""}
          onClose={() => { setSlotsFor(null); setSlotsCurrent(null); }}
          onSaved={async () => {
            setSlotsFor(null);
            setSlotsCurrent(null);
            await load();
          }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Sous-composants
// ─────────────────────────────────────────────────────────────────────────

const DaySection = ({ day, events, conflictKeys, onClick }) => (
  <section data-testid={`planning-day-${day}`}>
    <div className="flex items-baseline gap-3 mb-4">
      <div className="flex items-center gap-2">
        <div
          className={[
            "h-9 w-9 rounded-2xl flex items-center justify-center text-[12px] font-semibold tabular-nums",
            isTodayISO(day)
              ? "bg-[#C8FF2F] text-[#0A0A0A]"
              : "bg-[#0A0A0A] text-white dark:bg-white dark:text-[#0A0A0A]",
          ].join(" ")}
        >
          {new Date(day).getDate()}
        </div>
        <h2 className="text-[18px] sm:text-[20px] font-semibold tracking-[-0.02em] text-[#0A0A0A] dark:text-white capitalize">
          {fmtDayLong(day)}
        </h2>
      </div>
      <span className="text-[11.5px] text-[#9CA3AF] dark:text-[#525252] tabular-nums">
        · {events.length} livraison{events.length > 1 ? "s" : ""}
      </span>
      {isTodayISO(day) && (
        <Chip tone="accent" size="sm">Aujourd&apos;hui</Chip>
      )}
    </div>
    <div className="space-y-2.5">
      {events.map((ev) => (
        <EventCard
          key={ev.id}
          event={ev}
          conflict={conflictKeys.has(ev.id)}
          onClick={() => onClick(ev)}
        />
      ))}
    </div>
  </section>
);

const EventCard = ({ event, conflict, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`planning-event-${event.id}`}
    className={[
      "w-full text-left rounded-3xl border p-5 sm:p-6 group",
      "transition-all duration-200 ease-out",
      "flex items-center gap-5",
      "hover:-translate-y-0.5 hover:shadow-sm active:scale-[0.995]",
      conflict
        ? "border-[#FECACA] bg-[#FEF2F2] dark:bg-[#3B0F0F]/30 dark:border-[#3B0F0F]"
        : event.delivery_completed
        ? "border-[#EEF2F5] dark:border-[#262626] bg-[#F7F8FA] dark:bg-[#0A0A0A]"
        : "border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] hover:border-[#0A0A0A]/20 dark:hover:border-white/20",
    ].join(" ")}
  >
    {/* Heure XL prominent */}
    <div className="shrink-0 text-center min-w-[70px]">
      <p className="text-[28px] sm:text-[32px] font-semibold tabular-nums leading-none tracking-[-0.02em] text-[#0A0A0A] dark:text-white">
        {(event.time || "—").replace(":", "h")}
      </p>
      {conflict && (
        <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#EF4444] text-white px-2 py-0.5 text-[9.5px] font-semibold">
          <AlertTriangle size={9} strokeWidth={2.5} />
          Conflit
        </p>
      )}
    </div>

    {/* Vignette véhicule */}
    <div className="hidden sm:block shrink-0 h-16 w-24 rounded-2xl bg-[#F3F4F6] dark:bg-[#1A1A1A] overflow-hidden">
      {event.vehicle_image ? (
        <img src={event.vehicle_image} alt={event.title} className="h-full w-full object-cover" />
      ) : (
        <div className="h-full w-full flex items-center justify-center text-[#D1D5DB]">
          <Truck size={20} strokeWidth={1.5} />
        </div>
      )}
    </div>

    {/* Identité + méta */}
    <div className="flex-1 min-w-0">
      <h3 className="text-[15px] sm:text-[16px] font-semibold text-[#0A0A0A] dark:text-white tracking-[-0.01em] truncate">
        {event.title}
      </h3>
      <p className="mt-0.5 text-[12.5px] text-[#6B7280] dark:text-[#A3A3A3] truncate flex items-center gap-1.5">
        <User size={11} strokeWidth={2} className="shrink-0" />
        {event.client_name || "—"}
      </p>
      {event.location && (
        <p className="mt-1 text-[11.5px] text-[#9CA3AF] dark:text-[#525252] truncate flex items-center gap-1.5">
          <MapPin size={10} strokeWidth={2} className="shrink-0" />
          {event.location}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-1.5">
        <StatusChip event={event} />
      </div>
    </div>

    <ChevronRight
      size={18}
      strokeWidth={2}
      className="hidden sm:block text-[#9CA3AF] group-hover:text-[#0A0A0A] dark:group-hover:text-white transition-colors shrink-0"
    />
  </button>
);

const EmptyState = () => (
  <div
    className="rounded-3xl border border-dashed border-[#EEF2F5] dark:border-[#262626] p-10 text-center"
    data-testid="planning-empty"
  >
    <div className="mx-auto h-12 w-12 rounded-2xl bg-[#F3F4F6] dark:bg-[#1A1A1A] text-[#6B7280] dark:text-[#A3A3A3] flex items-center justify-center">
      <Calendar size={20} strokeWidth={1.75} />
    </div>
    <p className="mt-4 text-[16px] font-semibold text-[#0A0A0A] dark:text-white tracking-[-0.01em]">
      Aucune livraison sur cette période
    </p>
    <p className="mt-1 text-[13px] text-[#6B7280] dark:text-[#A3A3A3] max-w-md mx-auto">
      Les livraisons apparaissent ici automatiquement dès qu&apos;un client confirme un créneau ou qu&apos;une date est fixée sur un dossier.
    </p>
  </div>
);

export default Planning;
